/* eslint-disable react/require-default-props */
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Tooltip } from '@ui';
import { translate } from '@utils/i18n';
import { Popup } from '../../Popup/Popup';
import { Checkbox } from '../../inputs/Checkbox';
import './FilterToggleStyles.scss';
const namespace = 'ui.filter_toggle';
function translateWithFallback(key, fallback) {
    const translated = translate(key, { namespace });
    return translated === key ? fallback : translated;
}
export function sortManageFilterItems(items) {
    return [...items].sort((left, right) => {
        if (left.active !== right.active)
            return left.active ? -1 : 1;
        return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
    });
}
/**
 * FilterToggle Component
 * Funnel-icon anchor button + Quimby Popup + searchable checkbox list.
 * Port of legacy `frontend/components/FilterPanel/nested/FilterToggle.jsx`
 * (Kendo Popup replaced with Quimby Popup).
 * @version 1.3.0
 * @since 2026-05-04
 */
export function FilterToggle({ items }) {
    const manageFilterLabel = translateWithFallback('manage_filters_label', 'Manage filter');
    const searchPlaceholder = translateWithFallback('search_placeholder', 'Search');
    const noResultsLabel = translateWithFallback('no_results', 'No options found');
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const anchorRef = useRef(null);
    const visibleItems = useMemo(() => {
        const query = search.trim().toLowerCase();
        const filtered = query ? items.filter((item) => item.label.toLowerCase().includes(query)) : items;
        return sortManageFilterItems(filtered);
    }, [items, search]);
    useEffect(() => {
        if (!isOpen)
            setSearch('');
    }, [isOpen]);
    const handleToggle = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);
    const handleClose = useCallback(() => {
        var _a;
        setIsOpen(false);
        (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    return (React.createElement("div", { className: "qmb-ui-filter-toggle" },
        React.createElement(Tooltip, { content: manageFilterLabel, position: "top" },
            React.createElement(Button, { ref: anchorRef, className: "qmb-ui-filter-toggle__anchor", onClick: handleToggle, "aria-expanded": isOpen, "aria-haspopup": "dialog", "aria-label": manageFilterLabel },
                React.createElement("i", { className: "fa-light fa-filter", "aria-hidden": "true" }))),
        React.createElement(Popup, { anchor: anchorRef.current, show: isOpen, onClose: handleClose, position: "bottom-right", collision: { horizontal: 'fit', vertical: 'flip' }, containerClass: "qmb-ui-filter-toggle__popup" },
            React.createElement(Popup.SearchInput, { value: search, onChange: setSearch, placeholder: searchPlaceholder }),
            React.createElement(Popup.Section, { fillHeight: true },
                React.createElement("ul", { className: "qmb-ui-filter-toggle__list", role: "presentation" },
                    visibleItems.length === 0 && React.createElement("li", { className: "qmb-ui-filter-toggle__empty" }, noResultsLabel),
                    visibleItems.map((item) => (React.createElement("li", { key: item.field, className: "qmb-ui-filter-toggle__item" },
                        React.createElement(Checkbox, { id: `qmb-ui-filter-toggle__${item.field}`, checked: item.active, disabled: item.locked, onChange: () => item.onToggle(), label: React.createElement("span", { title: item.tooltip }, item.label) })))))))));
}
